import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  useAuth,
} from '@aaas/shared';
import recruitmentTypeService from '../services/recruitment-type.service';
import type {
  RecruitmentType,
  RecruitmentTypeCreateInput,
  RecruitmentTypeUpdateInput,
} from '../types/recruitment-type';

interface RecruitmentTypeFormDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  recruitmentType?: RecruitmentType | null;
  onSuccess: () => void;
}

export function RecruitmentTypeFormDialog({
  open,
  onClose,
  mode,
  recruitmentType,
  onSuccess,
}: RecruitmentTypeFormDialogProps) {
  const { user, currentWorkspace } = useAuth();
  const { t } = useTranslation('ats');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens or recruitmentType changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && recruitmentType) {
        setName(recruitmentType.name);
      } else {
        setName('');
      }
      setError(null);
    }
  }, [open, mode, recruitmentType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !currentWorkspace) return;
    if (!name.trim()) {
      setError(t('recruitmentType.nameLabel'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await user.getIdToken();

      if (mode === 'create') {
        const data: RecruitmentTypeCreateInput = {
          name: name.trim(),
        };
        await recruitmentTypeService.createRecruitmentType(currentWorkspace.id, token, data);
      } else if (recruitmentType) {
        const data: RecruitmentTypeUpdateInput = {
          name: name.trim(),
        };
        await recruitmentTypeService.updateRecruitmentType(currentWorkspace.id, recruitmentType.id, token, data);
      }

      onSuccess();
      onClose();
    } catch (err: unknown) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} recruitment type:`, err);
      const errorMessage = err instanceof Error ? err.message : t('config.unknownError');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create'
              ? t('recruitmentType.createTitle')
              : t('recruitmentType.editTitle')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t('recruitmentType.nameLabel')}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('recruitmentType.namePlaceholder')}
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              {t('config.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('config.creating')}
                </>
              ) : mode === 'create' ? (
                t('recruitmentType.createButton')
              ) : (
                t('recruitmentType.updateButton')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
